import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

interface AddNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId?: string;
  customerId?: string;
  onSaved?: () => void;
}

export function AddNoteDialog({
  open,
  onOpenChange,
  orderId,
  customerId,
  onSaved,
}: AddNoteDialogProps) {
  const { user } = useAuth();
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!content.trim()) {
        throw new Error("Sisältö on pakollinen");
      }

      const { error } = await supabase.from("communication_logs").insert({
        order_id: orderId || null,
        customer_id: customerId || null,
        communication_type: "note",
        direction: "internal",
        subject: subject.trim() || null,
        content: content.trim(),
        summary: content.trim().slice(0, 100) + (content.length > 100 ? "..." : ""),
        status: "completed",
        created_by: user?.id || null,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Muistiinpano lisätty");
      resetForm();
      onOpenChange(false);
      onSaved?.();
    },
    onError: (error) => {
      toast.error("Virhe", {
        description: error instanceof Error ? error.message : "Muistiinpanon tallennus epäonnistui",
      });
    },
  });

  const resetForm = () => {
    setSubject("");
    setContent("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Lisää muistiinpano</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="note-subject">Otsikko</Label>
            <Input
              id="note-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="esim. Asiakkaan toive"
            />
          </div>

          <div>
            <Label htmlFor="note-content">Sisältö *</Label>
            <Textarea
              id="note-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Kirjoita muistiinpano..."
              rows={6}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Peruuta
            </Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? "Tallennetaan..." : "Tallenna"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
